// Staleness calculation utilities for the "Last tested X ago" reminder banner.
// Determines how stale the latest measurement is and returns display metadata.

/**
 * Staleness level constants
 */
export const STALENESS_LEVEL = {
  FRESH: 'fresh',     // < 24 hours — no banner needed
  RECENT: 'recent',   // 24 hours to < 2 days — green
  STALE: 'stale',     // 2 to 4 days — yellow/warning
  OVERDUE: 'overdue'  // > 4 days — red/error
}

/**
 * Map staleness levels to MUI severity values for Alert component
 */
export const STALENESS_SEVERITY = {
  [STALENESS_LEVEL.FRESH]: 'success',
  [STALENESS_LEVEL.RECENT]: 'success',
  [STALENESS_LEVEL.STALE]: 'warning',
  [STALENESS_LEVEL.OVERDUE]: 'error'
}

/**
 * Calculate the staleness level of a measurement date relative to now.
 *
 * @param {string|Date} measuredAt - The date of the last measurement
 * @param {Date} [now] - Current time (injectable for testing)
 * @returns {string} One of STALENESS_LEVEL values
 */
export const calculateStaleness = (measuredAt, now = new Date()) => {
  if (!measuredAt) {
    return STALENESS_LEVEL.OVERDUE
  }

  const measureDate = new Date(measuredAt)
  if (isNaN(measureDate.getTime())) {
    return STALENESS_LEVEL.OVERDUE
  }

  const diffMs = now.getTime() - measureDate.getTime()

  // Future dates treated as fresh
  if (diffMs < 0) {
    return STALENESS_LEVEL.FRESH
  }

  const diffHours = diffMs / (1000 * 60 * 60)

  if (diffHours < 24) {
    return STALENESS_LEVEL.FRESH
  } else if (diffHours < 48) {
    return STALENESS_LEVEL.RECENT
  } else if (diffHours < 96) {
    return STALENESS_LEVEL.STALE
  } else {
    return STALENESS_LEVEL.OVERDUE
  }
}

/**
 * Format a relative time string for display on the reminder banner.
 * Returns human-readable strings like "1 day ago", "3 days ago", "1 week ago".
 *
 * @param {string|Date} measuredAt - The date of the last measurement
 * @param {Date} [now] - Current time (injectable for testing)
 * @returns {string} Relative time description
 */
export const formatRelativeTime = (measuredAt, now = new Date()) => {
  if (!measuredAt) {
    return 'unknown'
  }

  const measureDate = new Date(measuredAt)
  if (isNaN(measureDate.getTime())) {
    return 'unknown'
  }

  const diffMs = now.getTime() - measureDate.getTime()

  if (diffMs < 0) {
    return 'just now'
  }

  const diffHours = diffMs / (1000 * 60 * 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffHours < 1) {
    return 'less than an hour ago'
  } else if (diffHours < 24) {
    const hours = Math.floor(diffHours)
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  } else if (diffDays === 1) {
    return '1 day ago'
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else if (diffDays < 14) {
    return '1 week ago'
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7)
    return `${weeks} weeks ago`
  } else if (diffDays < 60) {
    return '1 month ago'
  } else {
    const months = Math.floor(diffDays / 30)
    return `${months} months ago`
  }
}

/**
 * Build the reminder banner message based on staleness.
 *
 * @param {string} relativeTime - Output from formatRelativeTime
 * @param {string} stalenessLevel - One of STALENESS_LEVEL values
 * @returns {string} The banner message text
 */
export const buildReminderMessage = (relativeTime, stalenessLevel) => {
  switch (stalenessLevel) {
    case STALENESS_LEVEL.RECENT:
      return `Last tested ${relativeTime} -- your water looks good!`
    case STALENESS_LEVEL.STALE:
      return `Last tested ${relativeTime} -- time to check your water!`
    case STALENESS_LEVEL.OVERDUE:
      return `Last tested ${relativeTime} -- your pool needs attention!`
    default:
      return `Last tested ${relativeTime}.`
  }
}
